import { createAsrRequest, queryTaskRequest } from '@ve-pro/api/aside'
import {
  AsrModel,
  AsrRes,
  QueryAsrModel,
  QueryAsrResModel,
  SentencesModel,
} from '@ve-pro/api/aside/type'
import { useHistoryStore, useProjectStore, useTrackStore } from '@ve-pro/store'
import { bathGenerateSegmentIdFromService } from '@ve-pro/store/tracks/generate-id'
import {
  resolveSegmentProperties,
  resolveSegmentText,
} from '@ve-pro/store/tracks/resolve-segment'
import { ref, reactive } from 'vue'
import {
  addSegment,
  insertAfterTrack,
} from '@ve-pro/store/tracks/tracks-actions'
import { getTrackTypeBySegmentType } from '@ve-pro/ve/timeline/track/track-height'
import {
  Segment,
  SegmentCollection,
  SegmentText,
  Track,
} from '@ve-pro/types/track.type'
import { MessageBox, Notification, Button } from 'element-ui'
import { t } from '@ve-pro/ve/lang'
import { h } from '@ve-pro/utils/h'
import { ElNotificationComponent } from 'element-ui/types/notification'
import { add, minus } from '@com/utils/math'
import { setTrackMaxUnlimited } from '@ve-pro/ve/timeline/track/track-expect-insert'
import { showInsertSuccess } from '@ve-pro/ve/timeline/track/track-message'
import { TrackType } from '@ve-pro/enums/track-type'
import { AiTaskStatus, AssetType } from '@ve-pro/enums/asset'
import { getConflictSegment } from '@ve-pro/ve/timeline/segment'
import { Orientation, RatioType } from 'video-editor-renderer'
import { veproLogs } from '@com/utils/report-logs/ve-pro'
import { LogKeys } from '@com/utils/report-logs'
import { getFullFrameMs } from '@ve-pro/store/tracks/frame'
import { SegmentType } from '@ve-pro/enums/segment'
import _ from 'lodash'

const DEFAULT_SUBTITLE_Y = {
  [RatioType['16:9']]: -200,
  [RatioType['9:16']]: -600,
  [RatioType['4:3']]: -300,
  [RatioType['3:4']]: -500,
  [RatioType['4:5']]: -500,
  [RatioType['2:1']]: -700,
  [RatioType['1:1']]: -350,
}

export const showFullLoading = ref(false)
export let pollClock: ReturnType<typeof setInterval> | null = null
export const failAsrList = ref<QueryAsrResModel[]>([])
let requestSegments: SegmentCollection[] = []
let notification: ElNotificationComponent | null = null
export const formValue = reactive<{
  language: string
  trackId: string
  trackIds: string[]
  isCnEn: boolean
}>({
  language: 'zh-cn',
  trackId: '',
  trackIds: [],
  isCnEn: false,
})

export const clearPollInterval = () => {
  if (pollClock) {
    clearInterval(pollClock)
    pollClock = null
  }
}

const pollTaskResultPromise = (
  taskIds: AsrRes['ids'],
  interval: number,
): ReturnType<typeof queryTaskRequest> => {
  return new Promise((resolve, reject) => {
    pollClock = setInterval(async () => {
      try {
        const res = await queryTaskRequest({
          ids: taskIds,
        })
        switch (true) {
          // 任务完成
          case res.data.data &&
            res.data.data.every(
              (item) =>
                item.status === AiTaskStatus.Success ||
                item.status === AiTaskStatus.Failed,
            ):
            if (pollClock) {
              resolve(res)
            }
            clearPollInterval()
            break
          default:
            break
        }
      } catch (error) {
        clearPollInterval()
        reject(error)
      }
    }, interval)
  })
}

export function getAsrTracks(): Track[] {
  const trackStore = useTrackStore()
  // 获取主轨道 + 其他资源轨道结构
  const tracks =
    [
      ...trackStore.defaultTracks.filter((track) =>
        trackStore.isMainTrack(track),
      ),
      ...trackStore.defaultTracks
        .filter(
          (track) =>
            (track.type === TrackType.Audio ||
              track.type === TrackType.Video) &&
            !trackStore.isMainTrack(track),
        )
        .reverse(),
    ] || []
  // 判断轨道中元件必须最少包含一个视频、音频元件  视频/音频组都不进行识别
  return _.cloneDeep(tracks).filter((track) => {
    if (track.type === TrackType.Video) {
      track.segments = track.segments.filter((item) => {
        return (
          item.type === SegmentType.Video &&
          item.asset_type === AssetType.Video &&
          !item.isGroup
        )
      })
    }
    if (track.type === TrackType.Audio) {
      track.segments = track.segments.filter((item) => {
        return (
          item.type === SegmentType.Audio &&
          item.asset_type === AssetType.Audio &&
          !item.isGroup
        )
      })
    }
    console.log('track.segments', track.segments)
    return (
      (track.type === TrackType.Audio ||
        (track.type === TrackType.Video &&
          track.segments.some(
            (item) =>
              item.type === SegmentType.Video &&
              item.asset_type === AssetType.Video,
          ))) &&
      track.segments.length > 0
    )
  })
}

const resolveSegmentByAsr = async (asrModels: QueryAsrModel[]) => {
  const sentences: SentencesModel[] = []
  const trackStore = useTrackStore()
  const historyStore = useHistoryStore()
  asrModels.forEach((asrModel) => {
    if (asrModel.sentences) {
      asrModel.sentences.forEach((item) => {
        sentences.push({
          begin_time: item.begin_time,
          end_time: item.end_time,
          text: item.text,
          id: asrModel.id,
        })
      })
    }
  })
  const segment = resolveSegmentText()
  const projectSegments = await bathGenerateSegmentIdFromService(
    [segment],
    sentences.map((item) => item.text),
  )
  // 在所有轨道最后插入一个轨道
  const createTrackAfterAllTracks = () => {
    // AI轨道选取逻辑保证该流程一定存在轨道
    const lastTrack =
      trackStore.subtitleTracks[trackStore.subtitleTracks.length - 1] ??
      trackStore.tracks[trackStore.tracks.length - 1]
    const reset = setTrackMaxUnlimited()
    const track = insertAfterTrack(
      getTrackTypeBySegmentType(segment.type),
      lastTrack!,
    )
    reset()
    return track
  }
  const newTrack = createTrackAfterAllTracks()
  let conflictTrack: Track | null = null
  projectSegments.forEach((segment, index) => {
    const sentence = sentences[index]!
    const sentenceSegment = requestSegments.find(
      (item) => item.id === sentence.id,
    )!
    const temp = resolveSegmentText()
    resolveSegmentProperties(temp)
    setDefaultSubtitleStyle(temp)
    temp.id = segment.id
    const start_time = add(sentenceSegment.start_time, sentence.begin_time)
    // 按时处理
    temp.start_time = getFullFrameMs(start_time)
    const startFrameOffset = Math.abs(start_time - temp.start_time)
    temp.duration = getFullFrameMs(
      minus(sentence.end_time, sentence.begin_time) + startFrameOffset,
    )
    temp.text = segment.text
    const conflictSegment = getConflictSegment(temp, newTrack)
    if (!conflictSegment) {
      addSegment(temp, newTrack)
    } else if (conflictTrack) {
      addSegment(temp, conflictTrack)
    } else {
      conflictTrack = createTrackAfterAllTracks()
      addSegment(temp, conflictTrack)
    }
  })
  showInsertSuccess(t('asr.dialog.success'))
  historyStore.push()
}

export const getTotalTime = (segments: Segment[]) => {
  if (segments.length) {
    return (
      segments.map((item) => item.duration).reduce((a, b) => add(a, b)) / 1000
    )
  }
  return 0
}

export const handleCancelAsr = async () => {
  showFullLoading.value = false
  MessageBox.confirm(t('asr.cancleDialog.title'), t('asr.cancleDialog.desc'), {
    confirmButtonText: t('asr.cancleDialog.ok'),
    cancelButtonText: t('asr.cancleDialog.cancel'),
    customClass: 'pt-custom-confirm-message',
    center: true,
    type: 'warning',
  })
    .then(() => {
      clearPollInterval()
      showFullLoading.value = false
    })
    .catch(() => {
      if (pollClock) {
        showFullLoading.value = true
      }
    })
}

export const failNotify = (failArray: QueryAsrResModel[]): void => {
  const trackStore = useTrackStore()
  const failSegments = trackStore.segments.filter((item) =>
    failArray.some((el) => el.param_id === item.id),
  )

  const b = h(
    'span',
    {
      domProps: {
        style: 'color: var(--color-primary);',
      },
      attrs: {
        place: 'count',
      },
    },
    `${getTotalTime(failSegments)}s`,
  )
  const a = h(
    'i18n',
    {
      attrs: {
        path: 'asr.dialog.failNote',
        tag: 'span',
      },
    },
    [b],
  )

  notification = Notification({
    title: t('asr.dialog.failTitle'),
    message: h('p', [
      a,
      h(
        Button,
        {
          class: 'notYet',
          attrs: {
            size: 'small',
          },
          on: {
            click: () => {
              notification?.close()
            },
          },
        },
        t('asr.dialog.failCancel'),
      ),
      h(
        Button,
        {
          attrs: {
            size: 'small',
            type: 'primary',
          },
          on: {
            click: () => {
              notification?.close()
              createSubtitleByAsr(failSegments, formValue.language)
            },
          },
        },
        t('asr.dialog.failRetry'),
      ),
    ]),
    duration: 0,
    type: 'error',
    dangerouslyUseHTMLString: true,
    customClass: 'ai-task-fail-notify',
  })
}

function resetAsr() {
  notification?.close()
  failAsrList.value = []
  requestSegments = []
}

export async function createSubtitleByAsr(
  segments: SegmentCollection[],
  language: string,
) {
  const action = veproLogs.createAction({ scene: 'asr' })

  const projectStore = useProjectStore()
  resetAsr()
  showFullLoading.value = true
  requestSegments = segments
  const postdata = segments.map<AsrModel>((segment) => {
    return {
      audio_url: segment.material?.file_url || '',
      // 兼容变速 要1倍
      duration: segment.source_rate.duration || segment.duration, // add(segment.trim_start_time, segment.duration),
      id: segment.id,
      rate: segment.rate,
      src_language:
        formValue.isCnEn && language === 'zh-cn' ? 'zh-cn&en' : language,
      start_time: segment.trim_start_time,
    }
  })
  action.log({
    e: 'asr',
    et: 's',
  })
  const startTime = Date.now()
  const setLogEnd = (status: LogKeys['status'], extra?: LogKeys['extra']) => {
    action.log({
      e: 'asr',
      et: 'e',
      d: Date.now() - startTime,
      status,
      extra: extra ?? -1,
    })
  }
  try {
    const asrRequest = createAsrRequest({
      batch: postdata,
      source: 've',
    })
    await action.apiLog(asrRequest)
    const res = await asrRequest
    // 没有数据，作为失败处理
    if (!res.data.data) throw new Error('createAsrRequest data is null')

    const pollTask = pollTaskResultPromise(res.data.data.ids, 3000)
    action.log({
      e: 'poll',
      et: 's',
    })
    const pollTaskStartTime = Date.now()
    const pollTaskLogEnd = (status: LogKeys['status']) => {
      action.log({
        e: 'poll',
        et: 'e',
        d: Date.now() - pollTaskStartTime,
        status,
      })
    }
    pollTask
      .then(() => {
        pollTaskLogEnd(2)
      })
      .catch(() => {
        pollTaskLogEnd(1)
      })

    const pollRes = await pollTask
    // ai转换完成后，更新用户信息中的AI套餐数量
    await projectStore.getUser()
    const asrModels = pollRes.data.data
    if (!asrModels) throw new Error('asrModels is null')
    if (asrModels.some((item) => item.status === AiTaskStatus.Failed)) {
      failAsrList.value = asrModels.filter(
        (item) => item.status === AiTaskStatus.Failed,
      )
      failNotify(failAsrList.value)
      // 提示失败弹窗
    }
    if (asrModels.some((item) => item.status === AiTaskStatus.Success)) {
      // 生成字幕元件
      await resolveSegmentByAsr(
        asrModels
          .filter((item) => item.status === AiTaskStatus.Success)
          .map((item) => item.result) || [],
      )
    }

    const result: Record<any, any> = {}
    asrModels.forEach((item) => {
      result[item.id as unknown as any] = item.status
    })
    setLogEnd(2, { result })
  } catch (error) {
    setLogEnd(1)
    console.log(error)
    throw new Error(error as any)
  } finally {
    showFullLoading.value = false
  }
}

export function setDefaultSubtitleStyle(segment: SegmentText) {
  const projectStore = useProjectStore()

  let y = -600

  if (
    projectStore.orientationAdapter === Orientation.Landscape &&
    projectStore.width >= projectStore.height
  ) {
    y = DEFAULT_SUBTITLE_Y[projectStore.ratio]
  }

  segment.x = 0
  segment.y = y
}

export function setDefaultRatiosSubtitleStyle(segment: SegmentText) {
  const projectStore = useProjectStore()
  const y = -600

  _.forEach(segment.ratios, (data, ratio) => {
    if (data) {
      data.x = 0
      data.y = DEFAULT_SUBTITLE_Y['9:16'] || -600
    }
  })

  // if (
  //   projectStore.orientationAdapter === Orientation.Landscape &&
  //   projectStore.width >= projectStore.height
  // ) {
  //   y = DEFAULT_SUBTITLE_Y[projectStore.ratio]
  // }

  segment.x = 0
  segment.y = y
}
